import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import { styled, useTheme } from '@mui/material/styles';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import { useTranslation } from 'react-i18next';
import { Title } from '@bloomays-lib/ui.shared';

const EmptyTalentBoardHeader = () => {
  const theme = useTheme();
  const { t } = useTranslation('talentBoard');

  return (
    <Container>
      <Title
        textTitle={t('emptyTalentBoardCard.title')}
        variant="heading6"
        sx={{
          color: theme.palette.neutral.black,
          textAlign: 'left',
          fontSize: '28px',
          lineHeight: '39.2px',
          [theme.breakpoints.down('sm')]: {
            fontSize: '24px',
            lineHeight: '33.6px',
          },
        }}
      />
      <Description>{t('emptyTalentBoardCard.description')}</Description>
      <StepContainer>
        <Step>
          <StepIconWrapper>
            <StepIconContainer backgroundColor={theme.palette.red[20]}>
              <FindIcon />
            </StepIconContainer>
            <Line />
          </StepIconWrapper>
          <div>
            <StepTitle>{t('emptyTalentBoardCard.step1.title')}</StepTitle>
            <StepDescription dangerouslySetInnerHTML={{ __html: t('emptyTalentBoardCard.step1.description') }} />
          </div>
        </Step>
        <Step>
          <StepIconWrapper>
            <StepIconContainer backgroundColor={theme.palette.red[60]}>
              <ContentPasteIcon />
            </StepIconContainer>
            <Line />
          </StepIconWrapper>
          <div>
            <StepTitle>{t('emptyTalentBoardCard.step2.title')}</StepTitle>
            <StepDescription dangerouslySetInnerHTML={{ __html: t('emptyTalentBoardCard.step2.description') }} />
          </div>
        </Step>
        <Step>
          <StepIconWrapper>
            <StepIconContainer backgroundColor={theme.palette.red[20]}>
              <ThumbUpIcon />
            </StepIconContainer>
          </StepIconWrapper>
          <div>
            <StepTitle>{t('emptyTalentBoardCard.step3.title')}</StepTitle>
            <StepDescription dangerouslySetInnerHTML={{ __html: t('emptyTalentBoardCard.step3.description') }} />
          </div>
        </Step>
      </StepContainer>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  background: theme.palette.gradient.red,
  borderRadius: '15px',
  padding: '30px 20px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const Description = styled('p')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '14px',
  lineHeight: '19.6px',
  color: theme.palette.neutral.black,
  textWrap: 'nowrap',
}));

const StepContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '20px',
  marginTop: '20px',
  alignItems: 'start',
  justifyContent: 'start',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '15px',
  },
}));

const Step = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'row',
    gap: '10px',
  },
}));

const StepIconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '10px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginBottom: '0px',
  },
}));

const StepIconContainer = styled('div', { shouldForwardProp: (prop) => prop !== 'backgroundColor' })<{
  backgroundColor: string;
}>(({ theme, backgroundColor }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: backgroundColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '32px',
    height: '32px',
  },
}));

const StepTitle = styled('h3')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '21.6px',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  fontFamily: '"Poppins SemiBold", sans-serif',
}));

const StepDescription = styled('p')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '19.6px',
  color: theme.palette.neutral.black,
}));

const Line = styled('div')(({ theme }) => ({
  width: '100%',
  height: '2px',
  background: theme.palette.red[20],
  [theme.breakpoints.down('sm')]: {
    width: '2px',
    height: '100%',
  },
}));

const FindIcon = styled(FindInPageOutlinedIcon)(({ theme }) => ({
  color: theme.palette.red[40],
  width: '24px',
  height: '24px',
  [theme.breakpoints.down('sm')]: {
    width: '20px',
    height: '20px',
  },
}));

const ContentPasteIcon = styled(ContentPasteOutlinedIcon)(({ theme }) => ({
  color: theme.palette.neutral.white,
  width: '20px',
  height: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '16px',
    height: '16px',
  },
}));

const ThumbUpIcon = styled(ThumbUpAltOutlinedIcon)(({ theme }) => ({
  color: theme.palette.red[40],
  width: '20px',
  height: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '16px',
    height: '16px',
  },
}));

export default EmptyTalentBoardHeader;
